/**
 * Title-case a string for display.
 *
 * Lowercases the input first, replaces underscores/hyphens with spaces,
 * then capitalises every word boundary. This handles all-caps inputs
 * (``"DAMAC MAISON PRIVE A"`` → ``"Damac Maison Prive A"``) the same
 * way as lowercase ones (``"property_finder"`` → ``"Property Finder"``).
 *
 * Returns ``"-"`` for null / undefined / empty input.
 */
export function titleCase(value: string | null | undefined): string {
  if (!value) return "-";
  return String(value)
    .toLowerCase()
    .replace(/[_-]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Format a date as dd/mm/yyyy. Returns "—" if the input is falsy or
 * cannot be parsed. Accepts ISO strings, Date objects, or epoch-ms.
 *
 * Always uses the en-GB locale and explicit day/month/year parts so
 * the output is locale-stable regardless of the user's browser/OS
 * settings (the previous toLocaleDateString() default was rendering
 * MM/DD/YYYY for en-US viewers).
 */
export function formatDate(
  value: string | Date | number | null | undefined,
): string {
  if (value === null || value === undefined || value === "") return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/**
 * Format a date+time as dd/mm/yyyy HH:MM (24h). Same locale-stable
 * approach as formatDate.
 */
export function formatDateTime(
  value: string | Date | number | null | undefined,
): string {
  if (value === null || value === undefined || value === "") return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
}
